import type {
  NivelAcesso,
  PapelPontoFocal,
  SituacaoAtualizacao,
  TipoFonte,
} from '../form/enums.js';
import type { FieldIssue } from '../form/schema.js';

/** Contratos HTTP compartilhados entre a API e o front. */

export const API_PREFIX = '/api/v1';

export const API_ROUTES = {
  submissions: `${API_PREFIX}/submissions`,
  submissionById: (id: string) => `${API_PREFIX}/submissions/${id}`,
  submissionsExportCsv: `${API_PREFIX}/submissions/export.csv`,
  submissionsStats: `${API_PREFIX}/submissions/stats`,
  attachmentById: (id: string) => `${API_PREFIX}/attachments/${id}`,
  managementLogin: `${API_PREFIX}/management/login`,
  managementMe: `${API_PREFIX}/management/me`,
  managementUsers: `${API_PREFIX}/management/users`,
  managementUserById: (id: string) => `${API_PREFIX}/management/users/${id}`,
  health: '/health',
} as const;

/** Nomes dos campos do `multipart/form-data` em `POST /submissions`. */
export const MULTIPART_FIELDS = {
  payload: 'payload',
  files: 'files',
} as const;

export const ERROR_CODES = {
  validation: 'VALIDATION_ERROR',
  badRequest: 'BAD_REQUEST',
  unauthorized: 'UNAUTHORIZED',
  notFound: 'NOT_FOUND',
  payloadTooLarge: 'PAYLOAD_TOO_LARGE',
  unsupportedMediaType: 'UNSUPPORTED_MEDIA_TYPE',
  rateLimited: 'RATE_LIMITED',
  internal: 'INTERNAL_ERROR',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** Corpo padrão de toda resposta de erro da API. */
export interface ApiErrorBody {
  error: {
    code: ErrorCode | string;
    message: string;
    details?: FieldIssue[];
  };
}

export interface CreateSubmissionResponse {
  id: string;
  protocolo: string;
  createdAt: string;
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface SubmissionSummary {
  id: string;
  protocolo: string;
  secretaria: string;
  sigla: string | null;
  responsavel: string;
  totalFontes: number;
  totalAnexos: number;
  createdAt: string;
}

export interface KnowledgeSourceDto {
  position: number;
  nome: string;
  tipo: TipoFonte;
  onde: string;
  assuntos: string;
  atualizada: SituacaoAtualizacao;
  acesso: NivelAcesso | null;
  responsavel: string | null;
}

export interface FocalPointDto {
  papel: PapelPontoFocal;
  nome: string;
  cargo: string;
  email: string;
  whatsapp: string;
  servidorEfetivo: boolean | null;
}

export interface AttachmentDto {
  id: string;
  nome: string;
  mimeType: string;
  tamanhoBytes: number;
  sha256: string;
  downloadUrl: string;
}

export interface SubmissionDetail {
  id: string;
  protocolo: string;
  formVersion: string;
  createdAt: string;
  secretaria: { nome: string; sigla: string | null; responsavel: string; email: string };
  observacoes: string | null;
  fontes: KnowledgeSourceDto[];
  pontosFocais: FocalPointDto[];
  anexos: AttachmentDto[];
}

export interface SubmissionStats {
  totalSubmissions: number;
  totalSources: number;
  totalAttachments: number;
  submissionsLast7Days: number;
  averageSourcesPerSubmission: number;
  lastSubmissionAt: string | null;
  bySecretaria: Array<{ secretaria: string; sigla: string | null; total: number; fontes: number }>;
  bySourceType: Array<{ tipo: TipoFonte; total: number }>;
  byAccessLevel: Array<{ acesso: NivelAcesso | 'Não informado'; total: number }>;
}

export type ManagementRole = 'admin' | 'viewer';

export interface ManagementUser {
  id: string;
  nome: string;
  email: string;
  role: ManagementRole;
  active: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface ManagementSession {
  token: string;
  user: ManagementUser & { bootstrap: boolean };
}
